import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  RefreshCw,
  Inbox,
  CheckCircle2,
  Clock,
  Clock3,
  AlertTriangle,
  Banknote,
  DollarSign,
  SlidersHorizontal,
  Download,
  FileText,
  Ship,
  Upload,
  Calendar,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Table } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { FilterBar } from "../../components/ui/FilterBar";
import { KpiCard } from "../../components/ui/KpiCard";
import { Button } from "../../components/ui/Button";
import api from "../../services/api";
import { downloadInvoiceExport, exportDateStamp } from "../../services/invoiceExport";
import {
  PER_PAGE,
  STATUS_MAP,
  EMPTY_ADV,
  fmtDate,
  fmtAmount,
  fmtAmountShort,
  useDebounce,
  isOverdue,
  isLate,
  countActiveFilters,
  getDefaultReceivedRange,
  DEFAULT_RECEIVED_RANGE_DAYS,
} from "./utils";
import { InvoiceDrawer } from "./components/InvoiceDrawer";
import { InvoiceFilterModal } from "./components/InvoiceFilterModal";
import { UploadInvoiceModal } from "./components/UploadInvoiceModal";
import { useAuth } from "../../contexts/AuthContext";

const TABLE_ID = "invoices-reception";
// Grouped views load up to this many matching rows on one page.
const GROUPED_FETCH_LIMIT = 5000;

const DEFAULT_STATS = {
  total: 0,
  sentToErp: 0,
  notSent: 0,
  overdue: 0,
  late: 0,
  incomplete: 0,
  lateThresholdDays: 7,
  totalHT: 0,
  totalTTC: 0,
};

export default function InvoicesReceptionPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user, updatePreferences } = useAuth();
  const columnOrder = user?.preferences?.tableColumns?.[TABLE_ID];
  const canManageInvoices = user?.role === "ADMIN" || user?.role === "AGENT";

  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link support: the Dashboard's "View all" links land here with
  // ?status=<status> or ?incomplete=true to pre-filter the list.
  const initialStatus = searchParams.get("status");
  const initialIncomplete = searchParams.get("incomplete") === "true";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rowsRequestId = useRef(0);
  const [stats, setStats] = useState(DEFAULT_STATS);

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search);
  const [columnFilters, setColumnFilters] = useState({});
  const debouncedColumnFilters = useDebounce(columnFilters);

  const [advFilters, setAdvFilters] = useState(() => ({
    ...EMPTY_ADV,
    ...(initialStatus && STATUS_MAP[initialStatus] ? { statuses: [initialStatus] } : {}),
    incompleteOnly: initialIncomplete,
  }));
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dateRange, setDateRange] = useState(() => getDefaultReceivedRange());
  const isDefaultDateRange = useMemo(() => {
    const def = getDefaultReceivedRange();
    return dateRange.from === def.from && dateRange.to === def.to;
  }, [dateRange]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [groupMode, setGroupMode] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback((includePagination = true, searchValue = debouncedSearch, activeColumnFilters = debouncedColumnFilters) => {
    const params = new URLSearchParams();
    if (includePagination) {
      params.set("page", String(groupMode ? 1 : page));
      params.set("limit", String(groupMode ? GROUPED_FETCH_LIMIT : PER_PAGE));
    }
    if (filter !== "all") params.set("erpStatus", filter);
    if (searchValue.trim()) params.set("search", searchValue.trim());
    if (advFilters.supplier.trim())
      params.set("supplier", advFilters.supplier.trim());
    if (dateRange.from) params.set("receivedFrom", dateRange.from);
    if (dateRange.to) params.set("receivedTo", dateRange.to);
    if (advFilters.invoiceDateFrom)
      params.set("invoiceDateFrom", advFilters.invoiceDateFrom);
    if (advFilters.invoiceDateTo)
      params.set("invoiceDateTo", advFilters.invoiceDateTo);
    if (advFilters.dueDateFrom)
      params.set("dueDateFrom", advFilters.dueDateFrom);
    if (advFilters.dueDateTo) params.set("dueDateTo", advFilters.dueDateTo);
    if (advFilters.erpSentFrom)
      params.set("erpSentFrom", advFilters.erpSentFrom);
    if (advFilters.erpSentTo) params.set("erpSentTo", advFilters.erpSentTo);
    if (advFilters.statuses.length)
      params.set("statuses", advFilters.statuses.join(","));
    if (advFilters.lateOnly) params.set("lateOnly", "true");
    if (advFilters.incompleteOnly) params.set("incompleteOnly", "true");
    if (overdueOnly) params.set("overdueOnly", "true");
    const activeColumns = Object.entries(activeColumnFilters).map(([key, condition]) => ({ key, ...condition }));
    if (activeColumns.length) params.set("columnFilters", JSON.stringify(activeColumns));
    return params;
  }, [filter, debouncedSearch, page, advFilters, overdueOnly, dateRange, groupMode, debouncedColumnFilters]);

  const loadRows = useCallback(() => {
    const requestId = ++rowsRequestId.current;
    setLoading(true);
    setError(null);
    const params = buildParams();
    api
      .get(`/bureau-invoices?${params}`)
      .then((r) => {
        if (requestId !== rowsRequestId.current) return;
        setRows(r.data.rows ?? []);
        setTotal(r.data.total ?? 0);
        if (r.data.stats) setStats(r.data.stats);
      })
      .catch((e) => {
        if (requestId === rowsRequestId.current) setError(e.message);
      })
      .finally(() => {
        if (requestId === rowsRequestId.current) setLoading(false);
      });
  }, [buildParams]);

  async function exportCurrent() {
    setExporting(true);
    setError(null);
    try {
      const params = buildParams(false, search, columnFilters);
      params.set("locale", locale.startsWith("en") ? "en" : "fr");
      const name = `factures-export-${exportDateStamp()}.xlsx`;
      await downloadInvoiceExport(params, name);
    } catch (_) {
      setError(t("invoices.export_error"));
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (initialStatus || initialIncomplete) setSearchParams({}, { replace: true });
    // Consume the deep-link param once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFilterCount = countActiveFilters(advFilters);
  const lateThresholdDays = stats.lateThresholdDays;

  // Quick-filter state driven by the KPI cards: at most one is active at a
  // time, mirroring the All/Sent/Not-sent tabs. Clicking an already-active
  // card resets back to "all" (toggle behavior).
  const kpiKey =
    overdueOnly ? "overdue" : advFilters.incompleteOnly ? "incomplete" : advFilters.lateOnly ? "late" : filter === "all" ? "total" : filter;

  const applyKpiFilter = useCallback((key) => {
    setPage(1);
    if (key === kpiKey) {
      // Toggle off: back to "all", no quick filter.
      setFilter("all");
      setOverdueOnly(false);
      setAdvFilters((prev) => ({ ...prev, lateOnly: false, incompleteOnly: false }));
      return;
    }
    setOverdueOnly(key === "overdue");
    setAdvFilters((prev) => ({
      ...prev,
      lateOnly: key === "late",
      incompleteOnly: key === "incomplete",
    }));
    setFilter(key === "sent" || key === "not_sent" ? key : "all");
  }, [kpiKey]);

  function updateDateRange(partial) {
    setDateRange((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }

  function resetDateRange() {
    setDateRange(getDefaultReceivedRange());
    setPage(1);
  }

  const updateColumnFilter = useCallback((key, condition) => {
    setColumnFilters((previous) => {
      const next = { ...previous };
      if (condition === undefined) delete next[key];
      else next[key] = condition;
      return next;
    });
    setPage(1);
  }, []);

  const filterOnValue = useCallback((key, value, row) => {
    const isDate = ["postingDate", "actualDepartureDate", "receivedAt", "dueDate", "erpSentAt"].includes(key);
    const isAmount = key === "totalExclVAT" || key === "totalInclVAT";
    updateColumnFilter(key, {
      value: value == null || value === "" ? null : isDate ? String(value).slice(0, 10) : String(value),
      exact: true,
      ...(isAmount && value != null ? { currency: String(row.currencyCode || "MAD").toUpperCase() } : {}),
    });
  }, [updateColumnFilter]);

  const groupByVesselFn = useCallback(
    (row) => row.vesselName?.trim() || t("invoices.no_vessel_label"),
    [t],
  );

  const groupByBlFn = useCallback(
    (row) => row.billOfLadingNo?.trim() || t("invoices.no_bl_label"),
    [t],
  );

  const renderGroupHeader = useCallback(
    (groupName, groupRows) => {
      const byCurrency = new Map();
      groupRows.forEach((r) => {
        const cur = r.currencyCode || "MAD";
        byCurrency.set(cur, (byCurrency.get(cur) || 0) + (r.totalInclVAT || 0));
      });
      const amounts = Array.from(byCurrency.entries())
        .map(([cur, amt]) => fmtAmount(amt, cur))
        .join(" + ");
      return (
        <>
          <span>{groupName}</span>
          <span className="ml-2 font-normal text-[11px] text-slate-400">
            {t("invoices.group_invoice_count", { count: groupRows.length })} · {amounts}
          </span>
        </>
      );
    },
    [t],
  );

  const handleColumnOrderChange = useCallback(
    (newOrderKeys) => {
      const tableColumns = {
        ...(user?.preferences?.tableColumns || {}),
        [TABLE_ID]: newOrderKeys,
      };
      updatePreferences({ tableColumns });
    },
    [user, updatePreferences],
  );

  const columns = useMemo(
    () => [
      {
        key: "invoiceNo",
        filterType: "text",
        label: t("invoices.col_invoice_number"),
        render: (v) => (
          <span className="font-mono text-[12px] text-primary font-semibold whitespace-nowrap">
            {v}
          </span>
        ),
      },
      {
        key: "salesOrders",
        filterType: "text",
        filterValues: (row) => row.salesOrders?.map((order) => order.salesOrderNo).filter(Boolean),
        label: t("invoices.col_sales_order_no"),
        render: (v) => {
          const text = v?.map((o) => o.salesOrderNo).filter(Boolean).join(", ");
          return (
            <span
              className="font-mono text-[11.5px] text-slate-500 max-w-[160px] block truncate"
              title={text}
            >
              {text || "—"}
            </span>
          );
        },
      },
      {
        key: "billOfLadingNo",
        filterType: "text",
        label: t("invoices.col_bill_of_lading"),
        render: (v) => (
          <span className="font-mono text-[11.5px] text-slate-500 whitespace-nowrap">
            {v || "—"}
          </span>
        ),
      },
      {
        key: "containers",
        filterType: "text",
        filterValues: (row) => row.containers?.map((container) => container.containerNo).filter(Boolean),
        label: t("invoices.col_container_no"),
        render: (v) => {
          const text = v?.map((c) => c.containerNo).filter(Boolean).join(", ");
          return (
            <span
              className="font-mono text-[11.5px] text-slate-500 max-w-[160px] block truncate"
              title={text}
            >
              {text || "—"}
            </span>
          );
        },
      },
      {
        key: "shippingCompanyName",
        filterType: "text",
        label: t("invoices.col_shipping_company"),
        render: (v) => (
          <span className="font-medium text-slate-900 whitespace-nowrap">
            {v}
          </span>
        ),
      },
      {
        key: "emailSender",
        filterType: "text",
        label: t("invoices.col_sender"),
        render: (v) => (
          <span
            className="text-slate-500 text-[11.5px] max-w-[160px] block truncate"
            title={v}
          >
            {v}
          </span>
        ),
      },
      {
        key: "emailSubject",
        filterType: "text",
        label: t("invoices.col_subject"),
        render: (v) => (
          <span
            className="text-slate-600 max-w-[200px] block truncate text-[11.5px]"
            title={v}
          >
            {v}
          </span>
        ),
      },
      {
        key: "vesselName",
        filterType: "text",
        label: t("invoices.col_vessel"),
        render: (v) => (
          <span className="text-slate-700 whitespace-nowrap text-[11.5px]">
            {v || "—"}
          </span>
        ),
      },
      {
        key: "loadingPort",
        filterType: "text",
        label: t("invoices.col_loading_port"),
        render: (v) => (
          <span className="text-slate-600 whitespace-nowrap text-[11.5px]">
            {v || "—"}
          </span>
        ),
      },
      {
        key: "dischargePort",
        filterType: "text",
        label: t("invoices.col_discharge_port"),
        render: (v) => (
          <span className="text-slate-600 whitespace-nowrap text-[11.5px]">
            {v || "—"}
          </span>
        ),
      },
      {
        key: "postingDate",
        filterType: "date",
        label: t("invoices.col_posting_date"),
        render: (v) => (
          <span className="whitespace-nowrap text-slate-600">
            {fmtDate(v, locale)}
          </span>
        ),
      },
      {
        key: "actualDepartureDate",
        filterType: "date",
        label: t("invoices.col_departure_date"),
        render: (v) => (
          <span className="whitespace-nowrap text-slate-600">
            {fmtDate(v, locale)}
          </span>
        ),
      },
      {
        key: "receivedAt",
        filterType: "date",
        label: t("invoices.col_received_at"),
        render: (v, row) => {
          const late = isLate(row, lateThresholdDays);
          return (
            <span
              className={`whitespace-nowrap ${late ? "text-rose-600 font-medium" : "text-slate-600"}`}
            >
              {fmtDate(v, locale)}
              {late && <span className="ml-1 text-[10px] text-rose-500">↑</span>}
            </span>
          );
        },
      },
      {
        key: "dueDate",
        filterType: "date",
        label: t("invoices.col_due_date"),
        render: (v, row) => {
          const overdue = isOverdue(row);
          return (
            <span
              className={`whitespace-nowrap font-medium ${overdue ? "text-rose-600" : "text-slate-600"}`}
            >
              {fmtDate(v, locale)}
              {overdue && (
                <span className="ml-1 text-[10px] text-rose-500">↑</span>
              )}
            </span>
          );
        },
      },
      {
        key: "erpSentAt",
        filterType: "date",
        label: t("invoices.col_erp_sent_at"),
        render: (v) => (
          <span
            className={`whitespace-nowrap ${v ? "text-emerald-700 font-medium" : "text-slate-300"}`}
          >
            {fmtDate(v, locale)}
          </span>
        ),
      },
      {
        key: "totalExclVAT",
        filterType: "number",
        label: t("invoices.col_total_excl_vat"),
        align: "right",
        render: (v, row) => (
          <span className="tabular-nums text-slate-700">{fmtAmount(v, row.currencyCode)}</span>
        ),
      },
      {
        key: "totalInclVAT",
        filterType: "number",
        label: t("invoices.col_total_incl_vat"),
        align: "right",
        render: (v, row) => (
          <span className="tabular-nums font-semibold text-slate-900">
            {fmtAmount(v, row.currencyCode)}
          </span>
        ),
      },
      {
        key: "status",
        filterType: "select",
        filterOptions: Object.entries(STATUS_MAP).map(([value, config]) => ({ value, label: t(config.labelKey) })),
        label: t("invoices.col_status"),
        render: (v, row) => {
          const cfg = STATUS_MAP[v] || { variant: "info", labelKey: "" };
          return (
            <div className="flex items-center gap-1.5">
              <Badge variant={cfg.variant}>{t(cfg.labelKey)}</Badge>
              {row.isIncomplete && <Badge variant="warning">{t("invoices.incomplete_label")}</Badge>}
            </div>
          );
        },
      },
    ],
    [t, locale, lateThresholdDays],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[18px] font-bold text-slate-900">
            {t("invoices.page_title")}
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {t("invoices.page_subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={loadRows}
            className="h-8 w-8 grid place-items-center text-slate-500 hover:bg-slate-100 rounded-md transition"
          >
            <RefreshCw size={14} strokeWidth={1.75} />
          </button>
          {canManageInvoices && (
            <>
              <Button variant="secondary" size="sm" icon={Download} onClick={exportCurrent} loading={exporting}>
                {t("invoices.btn_export")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Upload}
                onClick={() => setUploadModalOpen(true)}
              >
                {t("invoices.btn_upload")}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8 gap-3">
        <KpiCard
          label={t("invoices.kpi_total")}
          value={stats.total}
          icon={Inbox}
          active={kpiKey === "total"}
          onClick={() => applyKpiFilter("total")}
        />
        <KpiCard
          label={t("invoices.kpi_sent_erp")}
          value={stats.sentToErp}
          icon={CheckCircle2}
          iconColor="text-emerald-600"
          sub={`${Math.round((stats.sentToErp / (stats.total || 1)) * 100)}%`}
          active={kpiKey === "sent"}
          onClick={() => applyKpiFilter("sent")}
        />
        <KpiCard
          label={t("invoices.kpi_not_sent")}
          value={stats.notSent}
          icon={Clock}
          iconColor="text-amber-500"
          active={kpiKey === "not_sent"}
          onClick={() => applyKpiFilter("not_sent")}
        />
        <KpiCard
          label={t("invoices.kpi_overdue")}
          value={stats.overdue}
          icon={AlertTriangle}
          iconColor={stats.overdue > 0 ? "text-rose-600" : "text-slate-400"}
          trend={stats.overdue > 0 ? "down" : undefined}
          trendLabel={
            stats.overdue > 0 ? t("invoices.overdue_label") : undefined
          }
          active={kpiKey === "overdue"}
          onClick={() => applyKpiFilter("overdue")}
        />
        <KpiCard
          label={t("invoices.kpi_late")}
          value={stats.late}
          icon={Clock3}
          iconColor={stats.late > 0 ? "text-rose-600" : "text-slate-400"}
          trend={stats.late > 0 ? "down" : undefined}
          trendLabel={stats.late > 0 ? t("invoices.late_label") : undefined}
          active={kpiKey === "late"}
          onClick={() => applyKpiFilter("late")}
        />
        <KpiCard
          label={t("invoices.kpi_incomplete")}
          value={stats.incomplete}
          icon={FileText}
          iconColor={stats.incomplete > 0 ? "text-amber-600" : "text-slate-400"}
          active={kpiKey === "incomplete"}
          onClick={() => applyKpiFilter("incomplete")}
        />
        <KpiCard
          label={t("invoices.kpi_total_ht")}
          value={fmtAmountShort(stats.totalHT)}
          icon={Banknote}
        />
        <KpiCard
          label={t("invoices.kpi_total_ttc")}
          value={fmtAmountShort(stats.totalTTC)}
          icon={DollarSign}
        />
      </div>

      <FilterBar
        groups={[
          {
            value: filter,
            onChange: (key) => {
              setFilter(key);
              setOverdueOnly(false);
              setAdvFilters((prev) => ({ ...prev, lateOnly: false, incompleteOnly: false }));
              setPage(1);
            },
            tabs: [
              {
                key: "all",
                label: t("invoices.filter_all"),
                count: stats.total,
              },
              {
                key: "sent",
                label: t("invoices.filter_sent"),
                count: stats.sentToErp,
              },
              {
                key: "not_sent",
                label: t("invoices.filter_not_sent"),
                count: stats.notSent,
              },
            ],
          },
        ]}
        search={search}
        onSearch={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        searchPlaceholder={t("invoices.search_placeholder")}
        actions={
          <>
            <div
              className={`h-9 pl-2.5 pr-1.5 flex items-center gap-1.5 rounded-lg border text-[12.5px] font-medium transition ${
                isDefaultDateRange
                  ? "border-slate-200 bg-white text-slate-600"
                  : "border-primary bg-primary/5 text-primary"
              }`}
              title={t("invoices.filter_received_period")}
            >
              <Calendar size={13} strokeWidth={1.75} className="flex-shrink-0" />
              <input
                type="date"
                value={dateRange.from}
                max={dateRange.to}
                onChange={(e) => updateDateRange({ from: e.target.value })}
                aria-label={t("invoices.filter_date_from")}
                className="bg-transparent outline-none text-[12.5px] w-[124px]"
              />
              <span className="text-slate-300">→</span>
              <input
                type="date"
                value={dateRange.to}
                min={dateRange.from}
                onChange={(e) => updateDateRange({ to: e.target.value })}
                aria-label={t("invoices.filter_date_to")}
                className="bg-transparent outline-none text-[12.5px] w-[124px]"
              />
              {!isDefaultDateRange && (
                <button
                  onClick={resetDateRange}
                  title={t("invoices.date_range_reset", { days: DEFAULT_RECEIVED_RANGE_DAYS })}
                  className="w-6 h-6 flex-shrink-0 grid place-items-center rounded-md text-primary hover:bg-primary/10"
                >
                  <RotateCcw size={12} strokeWidth={1.75} />
                </button>
              )}
            </div>
            <button
              onClick={() => {
                setGroupMode((mode) => mode === "vessel" ? null : "vessel");
                setPage(1);
              }}
              aria-pressed={groupMode === "vessel"}
              className={`h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12.5px] font-medium transition ${
                groupMode === "vessel"
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Ship size={13} strokeWidth={1.75} />
              {t("invoices.btn_group_by_vessel")}
            </button>
            <button
              onClick={() => {
                setGroupMode((mode) => mode === "bl" ? null : "bl");
                setPage(1);
              }}
              aria-pressed={groupMode === "bl"}
              className={`h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12.5px] font-medium transition ${
                groupMode === "bl"
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <FileText size={13} strokeWidth={1.75} />
              {t("invoices.btn_group_by_bl")}
            </button>
            <button
              onClick={() => setFilterModalOpen(true)}
              className={`h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12.5px] font-medium transition ${
                activeFilterCount > 0
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <SlidersHorizontal size={13} strokeWidth={1.75} />
              {t("invoices.btn_filters")}
              {activeFilterCount > 0 && (
                <span className="bg-primary text-white text-[10px] font-bold w-4 h-4 rounded-full grid place-items-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </>
        }
      />

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-[13px] text-rose-700">
          {error}
        </div>
      )}

      <Table
        key={groupMode || "ungrouped"}
        columns={columns}
        rows={rows}
        rowKey="_id"
        onRowClick={setSelectedRow}
        loading={loading}
        page={groupMode ? undefined : page}
        total={groupMode ? undefined : total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        groupBy={
          groupMode === "vessel"
            ? groupByVesselFn
            : groupMode === "bl"
              ? groupByBlFn
              : undefined
        }
        renderGroupHeader={groupMode ? renderGroupHeader : undefined}
        columnFilters={columnFilters}
        onColumnFilterChange={updateColumnFilter}
        onFilterOnValue={filterOnValue}
        filterLabels={{
          filter: t("invoices.column_filter_placeholder"),
          empty: t("invoices.column_filter_empty"),
          clear: t("invoices.column_filter_clear"),
          onValue: t("invoices.filter_on_value"),
        }}
        emptyState={
          <div className="py-12 text-center">
            <FileText
              size={32}
              className="mx-auto text-slate-300 mb-3"
              strokeWidth={1}
            />
            <p className="text-[13px] font-medium text-slate-600">
              {t("invoices.empty_title")}
            </p>
            <p className="text-[12px] text-slate-400 mt-1">
              {t("invoices.empty_subtitle")}
            </p>
          </div>
        }
      />

      <InvoiceDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />

      <InvoiceFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        initialValues={advFilters}
        onApply={(vals) => {
          setAdvFilters(vals);
          setPage(1);
          setFilterModalOpen(false);
        }}
        onReset={() => {
          setAdvFilters(EMPTY_ADV);
          setPage(1);
          setFilterModalOpen(false);
        }}
      />

      <UploadInvoiceModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />
    </div>
  );
}
